/**
 * Theme: per-level palette fallbacks, hex parsing, and the small
 * `formatFrameTime` helper used by the replay HUD. Pure functions, no
 * Pixi or DOM imports — safe to use from any scene/module.
 */

import type { LevelData } from '../../gameplay/LevelLoader.js';

/** Per-level palette fallbacks: each entry = { top, bottom } as 0xRRGGBB. */
export const PALETTES: ReadonlyArray<{ top: number; bottom: number }> = [
  { top: 0x4a1f6e, bottom: 0x2a4a8a }, // dusk purple → indigo
  { top: 0x1f4a6e, bottom: 0x2a8a8a }, // ocean blue → teal
  { top: 0x6e2a4a, bottom: 0x2a1f6e }, // sunset rose → night
  { top: 0x2a6e4a, bottom: 0x1f3a4a }, // forest green → deep blue
  { top: 0x6e4a1f, bottom: 0x4a1f3a }, // amber → wine
  { top: 0x1f1f4a, bottom: 0x6e2a4a }, // midnight → magenta
  { top: 0x2a4a1f, bottom: 0x6e6e2a }, // moss → ochre
  { top: 0x4a2a6e, bottom: 0x1f6e6e }, // violet → cyan
  { top: 0x6e1f1f, bottom: 0x2a2a4a }, // crimson → slate
  { top: 0x1f3a1f, bottom: 0x0a1a2a }, // emerald → near-black
];

export function hexToInt(hex: string): number {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return parseInt(h, 16);
}

/** Pick the level's gradient: explicit `bgGradient` wins, otherwise a
 *  stable hash on `level.id` selects a palette so the same level always
 *  looks the same. */
export function resolveGradient(level: LevelData): { top: number; bottom: number } {
  if (level.bgGradient) {
    return { top: hexToInt(level.bgGradient.top), bottom: hexToInt(level.bgGradient.bottom) };
  }
  let h = 0;
  for (let i = 0; i < level.id.length; i++) h = (h * 31 + level.id.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length]!;
}

/** Format a fixed-step frame count as M:SS.cc (centiseconds). At 60Hz a
 *  frame is ~16.67ms, so two decimals (centiseconds) are the highest
 *  honest precision; fractions of a centi are shown rounded. */
export function formatFrameTime(frames: number): string {
  const totalMs = (frames * 1000) / 60;
  const totalCs = Math.round(totalMs / 10);
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const cs = totalCs % 100;
  const pad = (n: number, w = 2): string => n.toString().padStart(w, '0');
  return `${pad(m)}:${pad(s)}.${pad(cs)}`;
}
